// @flow

import React, { type ComponentType, type Node } from 'react';
import { compose, pure, withHandlers } from 'recompose';
import { getFieldValue } from '@catalytic/catalytic-ui';
import { type Evaluate, type Update } from '../Expression/ExpressionContext';
import Field from '../FieldV2/Field';
import { FIELD_PREFIX, FIELD_PREFIX_DIVIDER } from '../Field/FieldHelpers';
import { type Field as FieldType } from '../Field/FieldTypes';
import TopBarLoader from '../Loading/TopBarLoader';
import debounce from '../utils/debounce';

type Props = {
  evaluate: Evaluate,
  inputs: Array<FieldType>,
  loading: boolean,
  onChange?: (...args: Array<any>) => any,
  params: URLSearchParams,
  sessionValues: Array<{ id: string, value: ?any }>,
  update: Update
};

export const WebformFields = (props: Props): Node => {
  const {
    evaluate,
    inputs,
    loading,
    onChange,
    params,
    sessionValues,
    update
  } = props;
  const values = (inputs || []).map((input: FieldType) => {
    const { id, name, type, value: valueProp } = input;

    /*
     * Data source of truth priority order:
     * 1. Field value specified by the user
     * 2. Field value specified by session data
     * 3. Field value specified by a URL parameter
     * 4. Field value specified by the field's default value
     * https://github.com/catalyticlabs/triage/issues/5092
     */
    // Support optional chaining (https://github.com/facebook/flow/issues/4303)
    // $FlowIgnore
    const sessionValue = sessionValues?.find(data => data?.id === id)?.value;
    const paramsValues =
      // Support optional chaining (https://github.com/facebook/flow/issues/4303)
      // $FlowIgnore
      params?.getAll?.(`${FIELD_PREFIX}${FIELD_PREFIX_DIVIDER}${name}`) || [];
    const value = getFieldValue(
      sessionValue !== undefined
        ? sessionValue
        : paramsValues.length > 0
        ? paramsValues
        : valueProp,
      type
    );

    return { id, name, value };
  });
  const contextValues = (values || []).reduce((accumulator, currentValue) => {
    accumulator[currentValue?.name] = currentValue?.value;
    return accumulator;
  }, {});
  return loading ? (
    <TopBarLoader /> // Loading conditional fields
  ) : (
    (inputs || []).map((input: FieldType) => {
      const {
        condition,
        context,
        format,
        id,
        name,
        type,
        value: valueProp,
        ...props
      } = input;

      // Support optional chaining (https://github.com/facebook/flow/issues/4303)
      // $FlowIgnore
      const value = values?.find(data => data?.id === id)?.value;

      const handleChange = (...args: Array<any>) => {
        const [{ currentTarget }] = args;
        const { value: valueProp } = currentTarget || {};
        const value = getFieldValue(valueProp, type);

        // Update conditional fields
        debounce(
          () => {
            update(name, value);
          },
          { key: `${id}:update` }
        );

        if (typeof onChange === 'function') {
          onChange(...args);
        }
      };

      // Conditional fields
      if (typeof condition === 'string' && evaluate(condition) === false) {
        return;
      }

      return (
        <Field
          contextValues={contextValues}
          format={format}
          id={id}
          key={id}
          name={name}
          onChange={handleChange}
          type={type}
          update={update}
          value={value}
          {...props}
        />
      );
    })
  );
};
WebformFields.displayName = 'WebformFields';

type EnhancedProps = {
  evaluate: Evaluate,
  inputs: Array<FieldType>,
  loading: boolean,
  onChange?: (...args: Array<any>) => any,
  params: URLSearchParams,
  sessionValues: Array<{ id: string, value: ?any }>,
  update: Update
};

const EnhancedWebformFields: ComponentType<EnhancedProps> = compose(
  // Prevent re-render caused by passing functions through props.
  // TODO: Parent components should memoize callbacks.
  // https://reactjs.org/docs/hooks-reference.html#usecallback
  withHandlers({
    onChange: ({ onChange }) => (...args) => {
      if (typeof onChange === 'function') {
        return onChange(...args);
      }
    }
  }),
  pure
)(WebformFields);

export default EnhancedWebformFields;
